import { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from './components/Layout';
import { API_BASE_URL } from '../config/ApiConfig';
import { Link } from 'react-router-dom';
import PaginationBar from './components/PaginationBar';
import ResponsiveShadow from './components/ResponsiveShadow';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import useAtaModal from './components/hooks/useAtaModal';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import AtaModal from './components/modals/AtaModal';
import usePagination10 from './components/hooks/usePagination10';
import TableContainer from './components/TableContainer';
import DataTable from './components/DataTable';
import { formatDateView, formatTimeView, toIsoDate } from './utils/dateUtils';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import Toolbar from './components/Toolbar';
import LoadingModal from './components/modals/LoadingModal';

const initialAtaFormState = {
  cnpjEscola: '',
  data: '',
  hora: '',
  dataIni: '',
  dataFim: '',
  dataEdital: '',
  presidentePronome: '',
  presidenteNome: '',
  secretarioPronome: '',
  secretarioNome: '',
};

function Ata() {
  function handleEditAta() {
    if (selectedAtas.length !== 1) return;
    const ata = selectedAtas[0].ata;
    setAtaForm({ ...ata, data: toIsoDate(ata.data), dataIni: toIsoDate(ata.dataIni), dataFim: toIsoDate(ata.dataFim), dataEdital: toIsoDate(ata.dataEdital) });
    openAtaModal();
  }

  function handleNewAta() {
    setAtaForm({
      ...initialAtaFormState,
      cnpjEscola: cnpj || '',
    });
    openAtaModal();
  }
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');

  const [AtaForm, setAtaForm] = useState({
    ...initialAtaFormState,
    cnpjEscola: cnpj || '',
  });

  const [Atas, setAtas] = useState([]);
  const [atualizar, setAtualizar] = useState(false);
  const [selectedAtas, setSelectedAtas] = useState([]);

  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const { showAtaModal, openAtaModal, closeAtaModal } = useAtaModal();

  const totalItems = Atas.length;
  const { setPagination } = usePagination10(totalItems);
  const displayedAtas = Atas.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  const [isLoading, setLoading] = useState(true);

  useEffect(() => {
    if (!cnpj) {
      showDefaultModal('error', 'CNPJ não encontrado. Faça login novamente.');
      setLoading(false);
      return;
    }
    setLoading(true);
    axios
      .get(`${API_BASE_URL.ATA}/all`, {
        params: { cnpjEscola: cnpj },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        setAtas(result.data);
      })
      .catch(error => {
        console.error('Erro ao obter registros de atas:', error);
        showDefaultModal('error', 'Erro ao carregar atas.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [token, atualizar, cnpj]);

  function handleChange(event) {
    event.preventDefault();
    const { name, value } = event.target;
    let processedValue = value;

    if (
      [
        'presidentePronome',
        'presidenteNome',
        'secretarioPronome',
        'secretarioNome',
      ].includes(name)
    ) {
      processedValue = value.toUpperCase();
    }

    const newState = {
      ...AtaForm,
      [name]: processedValue,
    };

    if (name === 'dataIni' && value) {
      try {
        const dataIni = new Date(value);

        if (!isNaN(dataIni.getTime())) {
          const dataFim = new Date(dataIni);
          dataFim.setFullYear(dataFim.getFullYear() + 2);
          newState.dataFim = dataFim.toISOString().split('T')[0];

          const dataEdital = calcularDataEditalValida(dataIni);
          newState.dataEdital = dataEdital.toISOString().split('T')[0];
        }
      } catch (error) {
        console.error('Erro ao processar datas:', error);
      }
    }

    setAtaForm(newState);
  }

  function calcularDataEditalValida(dataInicio) {
    let data = new Date(dataInicio);
    let diasUteisEncontrados = 0;
    const MAX_TENTATIVAS = 10;

    while (diasUteisEncontrados < 2 && MAX_TENTATIVAS > 0) {
      data.setDate(data.getDate() - 1);

      if (isDiaUtil(data)) {
        diasUteisEncontrados++;
      }
    }

    return data;
  }

  function isDiaUtil(data) {
    if (data.getDay() === 0 || data.getDay() === 6) {
      return false;
    }

    const feriados = getFeriados(data.getFullYear());
    const dataStr = formatarDataParaComparacao(data);

    return !feriados.includes(dataStr);
  }

  function getFeriados(ano) {
    const feriadosFixos = [
      `01-01`,
      `04-21`,
      `05-01`,
      `09-07`,
      `10-12`,
      `11-02`,
      `11-15`,
      `12-25`,
    ];

    const pascoa = calcularPascoa(ano);
    const feriadosMoveis = [
      calcularDataRelativa(pascoa, -47),
      calcularDataRelativa(pascoa, -2),
      calcularDataRelativa(pascoa, 60),
    ];

    return [...feriadosFixos, ...feriadosMoveis];
  }

  function calcularPascoa(ano) {
    const a = ano % 19;
    const b = Math.floor(ano / 100);
    const c = ano % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const mes = Math.floor((h + l - 7 * m + 114) / 31) - 1;
    const dia = ((h + l - 7 * m + 114) % 31) + 1;

    return new Date(ano, mes, dia);
  }

  function calcularDataRelativa(dataBase, dias) {
    const novaData = new Date(dataBase);
    novaData.setDate(novaData.getDate() + dias);
    return formatarDataParaComparacao(novaData);
  }

  function formatarDataParaComparacao(data) {
    return `${String(data.getMonth() + 1).padStart(2, '0')}-${String(
      data.getDate()
    ).padStart(2, '0')}`;
  }

  function handleSelectAta(ata) {
    const ataKey = `${ata.cnpjEscola}-${ata.data}-${ata.hora}`;
    setSelectedAtas(prev => {
      if (prev.some(selected => selected.key === ataKey)) {
        return prev.filter(selected => selected.key !== ataKey);
      } else {
        return [...prev, { key: ataKey, ata }];
      }
    });
  }

  function handleSelectAll() {
    if (selectedAtas.length === displayedAtas.length) {
      setSelectedAtas([]);
    } else {
      const allSelected = displayedAtas.map(ata => ({
        key: `${ata.cnpjEscola}-${ata.data}-${ata.hora}`,
        ata,
      }));
      setSelectedAtas(allSelected);
    }
  }

  function isAtaSelected(ata) {
    const ataKey = `${ata.cnpjEscola}-${ata.data}-${ata.hora}`;
    return selectedAtas.some(selected => selected.key === ataKey);
  }

  function handleSubmit(event) {
    event.preventDefault();

    const payload = {
      ...AtaForm,
      cnpjEscola: cnpj,
    };

    if (!payload.cnpjEscola || !payload.data || !payload.hora) {
      showDefaultModal(
        'error',
        'CNPJ da Escola, Data da Ata e Hora da Ata são obrigatórios!'
      );
      return;
    }

    ['dataIni', 'dataFim', 'dataEdital'].forEach(field => {
      if (payload[field] === '') {
        payload[field] = null;
      }
    });

    console.log('Salvando Ata com payload:', payload);

    axios
      .post(`${API_BASE_URL.ATA}/save`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        setAtualizar(prev => !prev);
        setAtaForm({
          ...initialAtaFormState,
          cnpjEscola: cnpj,
        });
        closeAtaModal();
        showDefaultModal('success', 'Ata salva com sucesso!');
      })
      .catch(error => {
        console.error('Erro ao salvar Ata:', error);
        const errorMessage =
          error.response?.data?.message || 'Erro ao salvar a ata.';
        showDefaultModal('error', errorMessage);
      });
  }

  function handleDelete(ataToDelete) {
    if (!ataToDelete.cnpjEscola || ataToDelete.cnpjEscola !== cnpj) {
      showDefaultModal(
        'error',
        'Não é possível excluir esta ata. CNPJ não corresponde.'
      );
      return;
    }
    showConfirmModal('delete', () => handleConfirmDelete(ataToDelete));
  }

  function handleConfirmDelete(ataToDelete) {
    const dataPk = ataToDelete.data;
    const horaPk = ataToDelete.hora;

    axios
      .delete(`${API_BASE_URL.ATA}/delete`, {
        params: {
          cnpjEscola: ataToDelete.cnpjEscola,
          data: dataPk,
          hora: horaPk,
        },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        setAtualizar(prev => !prev);
        showDefaultModal('success', 'Ata excluída com sucesso!');
      })
      .catch(error => {
        console.error('Erro ao deletar Ata:', error);
        const errorMessage =
          error.response?.data?.message || 'Erro ao excluir a ata.';
        showDefaultModal('error', errorMessage);
      });
  }

  async function handlePrintAta(ataToPrint) {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL.ATA}/preencherAta`,
        ataToPrint,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          responseType: 'blob',
        }
      );
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `Ata.docx`;
      link.click();
    } catch (error) {
      console.error('Erro ao preencher o template:', error);
      showDefaultModal('error', 'Erro ao preencher o template.');
    } finally {
      setLoading(false);
    }
  }

  async function handlePrintEdital(ataToPrint) {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL.ATA}/preencherEdital`,
        ataToPrint,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          responseType: 'blob',
        }
      );
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `Edital.docx`;
      link.click();
    } catch (error) {
      console.error('Erro ao preencher o template:', error);
      showDefaultModal('error', 'Erro ao preencher o template.');
    } finally {
      setLoading(false);
    }
  }

  function handleBulkPrintAta() {
    if (selectedAtas.length === 0) {
      showDefaultModal('error', 'Selecione pelo menos uma ata para imprimir.');
      return;
    }

    selectedAtas.forEach(({ ata }) => {
      handlePrintAta(ata);
    });
  }

  function handleBulkPrintEdital() {
    if (selectedAtas.length === 0) {
      showDefaultModal(
        'error',
        'Selecione pelo menos uma ata para imprimir o edital.'
      );
      return;
    }

    selectedAtas.forEach(({ ata }) => {
      handlePrintEdital(ata);
    });
  }

  function handleBulkDelete() {
    if (selectedAtas.length === 0) {
      showDefaultModal('error', 'Selecione pelo menos uma ata para excluir.');
      return;
    }

    showConfirmModal('delete', () => {
      selectedAtas.forEach(({ ata }) => {
        handleConfirmDelete(ata);
      });
      setSelectedAtas([]);
    });
  }

  return (
    <Layout>
      <LoadingModal show={isLoading} />
      <ResponsiveShadow>
        <PageHeader title="Atas" icon="bi-file-text" />
        <PageContent>
          <Toolbar>
              <button className="btn btn-success btn-sm botao-igual" onClick={handleNewAta}>
                <i className="bi bi-plus-circle me-1"></i>
                Nova Ata
              </button>
              <button className="btn btn-warning btn-sm botao-igual" onClick={handleEditAta} disabled={selectedAtas.length !== 1}>
                <i className="bi bi-pencil-square me-1"></i>
                Editar
              </button>
              <button className="btn btn-outline-success btn-sm botao-igual" onClick={handleBulkPrintAta} disabled={selectedAtas.length === 0}>
                <i className="bi bi-printer me-1"></i>
                Imprimir Ata
              </button>
              <button className="btn btn-outline-info btn-sm botao-igual" onClick={handleBulkPrintEdital} disabled={selectedAtas.length === 0}>
                <i className="bi bi-printer me-1"></i>
                Imprimir Edital
              </button>
              <button className="btn btn-outline-danger btn-sm botao-igual" onClick={handleBulkDelete} disabled={selectedAtas.length === 0}>
                <i className="bi bi-trash-fill me-1"></i>
                Excluir
              </button>
          </Toolbar>
          <TableContainer isLoading={false}>
            <DataTable head={
              <tr>
                <th scope="col">Data</th>
                <th scope="col">Hora</th>
                <th scope="col">Data Início</th>
                <th scope="col">Data Fim</th>
                <th scope="col">Data Edital</th>
                <th scope="col">Presidente</th>
                <th scope="col">Secretário</th>
              </tr>
            }>
              <tbody>
                {!isLoading && displayedAtas.length > 0 ? (
                  displayedAtas.map(ata => (
                    <tr
                      key={`${ata.cnpjEscola}-${ata.data}-${ata.hora}`}
                      className={isAtaSelected(ata) ? 'table-primary' : ''}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSelectAta(ata)}
                    >
                      <td>{formatDateView(ata.data)}</td>
                      <td>{formatTimeView(ata.hora)}</td>
                      <td>{formatDateView(ata.dataIni)}</td>
                      <td>{formatDateView(ata.dataFim)}</td>
                      <td>{formatDateView(ata.dataEdital)}</td>
                      <td>{ata.presidenteNome || '-'}</td>
                      <td>{ata.secretarioNome || '-'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center">
                      {isLoading ? 'Carregando...' : 'Nenhuma ata encontrada.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </DataTable>
          </TableContainer>
        </PageContent>

        <PaginationBar setPagination={setPagination} />
      </ResponsiveShadow>

      <ConfirmModal {...setConfirmModal} />
      <DefaultModal {...setDefaultModal} />
      <AtaModal
        show={showAtaModal}
        onClose={closeAtaModal}
        onSubmit={handleSubmit}
        ataForm={AtaForm}
        handleChange={handleChange}
      />
    </Layout>
  );
}
export default Ata;
